/**
 * The Lab's **Hit shapes** view (63.3 step 2; lane F): every body's measured hit shape drawn over the scene in cyan
 * (constants/hitboxes.ts — what the sim will read from step 3), and what the sim reads today in orange — the walk /
 * cover cylinders of props and structures (core/surfaceWalk.ts) and each person's bullet sphere (or a crouched or
 * prone person's spheres, core/stance.ts). E5.2: a normal tree's measured wood (constants/treeWood.ts — the trunk, its
 * lean, its limbs; a felled one's stump and lying trunk) in cyan beside its walking cylinder. The owner checks the shapes against the models here; nothing in the sim
 * reads this. `?hitshapes=1`, or the Lab's row.
 */
import { structureHitbox, buildingsByFunction, ruinHitbox } from '../core/buildingScale';
import * as THREE from 'three';
import type { GameState, PlayerState, StructureState, Vec3 } from '../types';
import type { HitboxDef, HitPart } from '../types/body';
import { HITBOXES } from '../constants/hitboxes';
import { PERSON_BODY_HEIGHT, personSetOf } from '../core/scaleProfile';
import { planetObstacles, headingOf } from '../core/surfaceWalk';
import { getPlayerWorldPosition, getStructureWorldPosition } from '../core/gameState';
import { stanceSpheres } from '../core/stance';
import { hitboxCenter } from '../core/bullets';
import { surfacePropScale } from '../core/propScale';

export { setHitShapesShown, hitShapesShown } from './hitShapesFlag';

const NEW = new THREE.LineBasicMaterial({ color: 0x40e0ff, depthTest: false, transparent: true, opacity: 0.9 });
const OLD = new THREE.LineBasicMaterial({ color: 0xff9a3c, depthTest: false, transparent: true, opacity: 0.75 });

/** Line segments outlining a part (unit scale, in its own frame). */
function partLines(part: HitPart): number[] {
  const out: number[] = [];
  const seg = (a: number[], b: number[]) => out.push(...a, ...b);
  if (part.shape === 'box') {
    const [cx, cy, cz] = part.centre, [hx, hy, hz] = part.half;
    const c = (sx: number, sy: number, sz: number) => [cx + sx * hx, cy + sy * hy, cz + sz * hz];
    for (const s of [-1, 1]) for (const t of [-1, 1]) { seg(c(-1, s, t), c(1, s, t)); seg(c(s, -1, t), c(s, 1, t)); seg(c(s, t, -1), c(s, t, 1)); }
    return out;
  }
  const a = new THREE.Vector3(...part.a), b = new THREE.Vector3(...part.b), axis = b.clone().sub(a);
  const dir = axis.lengthSq() > 1e-12 ? axis.clone().normalize() : new THREE.Vector3(0, 1, 0);
  const u = new THREE.Vector3(1, 0, 0).cross(dir).lengthSq() > 1e-6 ? new THREE.Vector3(1, 0, 0).cross(dir).normalize() : new THREE.Vector3(0, 0, 1).cross(dir).normalize();
  const v = dir.clone().cross(u);
  const N = 16, r = part.radius;
  const at = (c: THREE.Vector3, ang: number, lift = 0) => c.clone().addScaledVector(u, Math.cos(ang) * r).addScaledVector(v, Math.sin(ang) * r).addScaledVector(dir, lift);
  for (const [c, cap] of [[a, -1], [b, 1]] as Array<[THREE.Vector3, number]>) {
    for (let i = 0; i < N; i++) seg(at(c, (i / N) * Math.PI * 2).toArray(), at(c, ((i + 1) / N) * Math.PI * 2).toArray());
    // the cap: two half circles over the end
    for (const w of [u, v]) for (let i = 0; i < N / 2; i++) {
      const p = (k: number) => { const ang = (k / (N / 2)) * Math.PI; return c.clone().addScaledVector(w, Math.cos(ang) * r).addScaledVector(dir, cap * Math.sin(ang) * r).toArray(); };
      seg(p(i), p(i + 1));
    }
  }
  for (let i = 0; i < 4; i++) seg(at(a, (i / 4) * Math.PI * 2).toArray(), at(b, (i / 4) * Math.PI * 2).toArray());
  return out;
}
/** A unit upright cylinder's outline (radius 1, from y 0 to 1): today's cover shape, scaled per obstacle. */
const CYLINDER: number[] = (() => {
  const out: number[] = [], N = 16;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2, b = ((i + 1) / N) * Math.PI * 2;
    for (const y of [0, 1]) out.push(Math.cos(a), y, Math.sin(a), Math.cos(b), y, Math.sin(b));
    if (i % 4 === 0) out.push(Math.cos(a), 0, Math.sin(a), Math.cos(a), 1, Math.sin(a));
  }
  return out;
})();

/** E5.2: a tree piece's outline in the world — a ring at either end (radius ra at a, rb at b) and four lines between. */
function frustumLines(a: THREE.Vector3, b: THREE.Vector3, ra: number, rb: number, out: number[]): void {
  const dir = b.clone().sub(a).normalize();
  const u = new THREE.Vector3(1, 0, 0).cross(dir);
  if (u.lengthSq() < 1e-6) u.set(0, 0, 1).cross(dir);
  u.normalize();
  const v = dir.clone().cross(u);
  const at = (c: THREE.Vector3, r: number, ang: number) => c.clone().addScaledVector(u, Math.cos(ang) * r).addScaledVector(v, Math.sin(ang) * r).toArray();
  const N = 10;
  for (const [c, r] of [[a, ra], [b, rb]] as Array<[THREE.Vector3, number]>) for (let i = 0; i < N; i++) out.push(...at(c, r, (i / N) * Math.PI * 2), ...at(c, r, ((i + 1) / N) * Math.PI * 2));
  for (let i = 0; i < 4; i++) out.push(...at(a, ra, (i / 4) * Math.PI * 2), ...at(b, rb, (i / 4) * Math.PI * 2));
}

function lines(parts: HitPart[], mat: THREE.LineBasicMaterial, raw?: number[]): THREE.LineSegments {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(raw ?? parts.flatMap(partLines), 3));
  const l = new THREE.LineSegments(g, mat);
  l.renderOrder = 10;
  l.frustumCulled = false;
  return l;
}

/** Place `obj` at `feet` with up along `up` and z along `forward` (projected onto the ground's plane), scaled by `scale`. */
function place(obj: THREE.Object3D, feet: Vec3, up: Vec3, forward: Vec3 | null, scale: number): void {
  const y = new THREE.Vector3(up.x, up.y, up.z).normalize();
  if (!forward) {
    // upright as the renderer seats a structure (the shortest turn from +y): the frame core/hits.ts lays its parts in
    obj.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), y);
    obj.position.set(feet.x, feet.y, feet.z);
    obj.scale.setScalar(scale);
    return;
  }
  let z = new THREE.Vector3(forward.x, forward.y, forward.z);
  z.addScaledVector(y, -z.dot(y));
  if (z.lengthSq() < 1e-9) z = new THREE.Vector3(0, 0, 1).cross(y);
  z.normalize();
  const x = y.clone().cross(z);
  obj.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, z));
  obj.position.set(feet.x, feet.y, feet.z);
  obj.scale.setScalar(scale);
}

export class HitShapesView {
  private readonly root = new THREE.Group();
  private statics = new THREE.Group();
  private staticKey: unknown[] = [];
  private readonly people = new Map<number, { group: THREE.Group; stance: string; now: THREE.LineSegments; then: THREE.LineSegments }>();

  constructor(private readonly scene: THREE.Scene) {
    this.root.add(this.statics);
    scene.add(this.root);
  }

  /** Each frame: the people where they stand; the structures and the cover cylinders when they change. */
  update(state: GameState): void {
    const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
    const planets = [...new Set(state.players.filter(p => p.alive).map(p => p.planetIndex))];
    const key: unknown[] = [state.structures, ...planets.map(pi => planetObstacles(state, pi))];
    if (key.length !== this.staticKey.length || key.some((k, i) => k !== this.staticKey[i])) { this.rebuildStatics(state, planets, person); this.staticKey = key; }
    const seen = new Set<number>();
    for (const u of state.players) {
      if (!u.alive) continue;
      seen.add(u.id);
      this.person(u, state, person);
    }
    for (const [id, p] of this.people) if (!seen.has(id)) { this.root.remove(p.group); p.now.geometry.dispose(); p.then.geometry.dispose(); this.people.delete(id); }
  }

  dispose(): void {
    this.scene.remove(this.root);
    this.root.traverse(o => { const l = o as THREE.LineSegments; if (l.isLineSegments) l.geometry.dispose(); });
    this.people.clear();
  }

  private rebuildStatics(state: GameState, planets: number[], person: number): void {
    this.root.remove(this.statics);
    this.statics.traverse(o => { const l = o as THREE.LineSegments; if (l.isLineSegments) l.geometry.dispose(); });
    this.statics = new THREE.Group();
    this.root.add(this.statics);
    for (const s of state.structures) this.statics.add(this.structureShape(s, state, person));
    // 63.7 (b)'s follow-up: a ruin's shape, where ruins are bodies (the buildings sized by function)
    if (buildingsByFunction(state.config)) for (const r of state.ruins) {
      const shape = lines(ruinHitbox(r).parts, NEW);
      place(shape, getStructureWorldPosition({ planetIndex: r.planetIndex, surfaceDir: r.surfaceDir } as StructureState, state.planets), r.surfaceDir, null, person);
      this.statics.add(shape);
    }
    // today's cover cylinders (props and structures), as the walk, the bullets and the sight test read them
    for (const pi of planets) {
      for (const o of planetObstacles(state, pi).list) {
        const cyl = lines([], OLD, CYLINDER);
        const ground = Math.hypot(...['x', 'y', 'z'].map(k => (o.dir as unknown as Record<string, number>)[k])) || 1;
        const d = { x: o.dir.x / ground, y: o.dir.y / ground, z: o.dir.z / ground };
        const feet = getStructureWorldPosition({ planetIndex: pi, surfaceDir: d } as StructureState, state.planets);
        place(cyl, feet, d, null, 1);
        cyl.scale.set(o.radius, o.height, o.radius);
        this.statics.add(cyl);
        // E5.2: a tree's drawn wood, as shots and sight meet it (cyan; walking still reads the orange cylinder)
        if (o.pieces?.length) {
          const raw: number[] = [];
          for (const pc of o.pieces) frustumLines(new THREE.Vector3(feet.x + pc.a.x, feet.y + pc.a.y, feet.z + pc.a.z), new THREE.Vector3(feet.x + pc.b.x, feet.y + pc.b.y, feet.z + pc.b.z), pc.ra, pc.rb, raw);
          this.statics.add(lines([], NEW, raw));
        }
      }
    }
  }

  private structureShape(s: StructureState, state: GameState, person: number): THREE.Object3D {
    const def: HitboxDef = structureHitbox(s, state.config);   // 63.7 (b): a building sized by function, its own model's
    const shape = lines(def.parts, NEW);
    const scale = def.ruler === 'prop' ? surfacePropScale(state.planets[s.planetIndex].radius) : person;
    place(shape, getStructureWorldPosition(s, state.planets), s.surfaceDir, null, scale);
    return shape;
  }

  private person(u: PlayerState, state: GameState, person: number): void {
    const ps = personSetOf(state);
    const def = u.body === 'person' || state.config.clock === 'action' ? HITBOXES.person : HITBOXES.unit;
    const stance = u.stance ?? 'stand';
    let p = this.people.get(u.id);
    if (!p || p.stance !== stance) {
      if (p) { this.root.remove(p.group); p.now.geometry.dispose(); p.then.geometry.dispose(); }
      const parts = stance === 'crouch' ? def.stances?.crouch ?? def.parts : stance === 'prone' ? def.stances?.prone ?? def.parts : def.parts;
      const group = new THREE.Group(), now = lines(parts, NEW), then = new THREE.LineSegments(new THREE.BufferGeometry(), OLD);
      then.frustumCulled = false; then.renderOrder = 10;
      group.add(now);
      this.root.add(group, then);
      p = { group, stance, now, then };
      this.people.set(u.id, p);
    }
    const feet = getPlayerWorldPosition(u, state.planets);
    place(p.group, feet, u.surfaceDir, headingOf(u), def.ruler === 'unit' ? PERSON_BODY_HEIGHT : person);
    // today's bullet volume: the sphere (or a crouched or prone person's spheres), drawn as rings in world space
    const n = new THREE.Vector3(u.surfaceDir.x, u.surfaceDir.y, u.surfaceDir.z).normalize(), h = headingOf(u);
    const spheres = !u.stance || u.stance === 'stand' || u.airborne
      ? [{ c: hitboxCenter(u, state.planets, ps.hitboxCenter, ps), r: ps.hitboxRadius }]
      : stanceSpheres(u.stance, ps).map(s => ({ c: { x: feet.x + n.x * s.up + h.x * s.along, y: feet.y + n.y * s.up + h.y * s.along, z: feet.z + n.z * s.up + h.z * s.along }, r: s.r }));
    const pts: number[] = [];
    for (const s of spheres) for (const axis of [0, 1, 2]) for (let i = 0; i < 16; i++) {
      const ring = (k: number) => { const a = (k / 16) * Math.PI * 2, c = Math.cos(a) * s.r, d = Math.sin(a) * s.r; return axis === 0 ? [s.c.x, s.c.y + c, s.c.z + d] : axis === 1 ? [s.c.x + c, s.c.y, s.c.z + d] : [s.c.x + c, s.c.y + d, s.c.z]; };
      pts.push(...ring(i), ...ring(i + 1));
    }
    p.then.geometry.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
  }
}
